import {
  Component,
  EventEmitter,
  inject,
  OnInit,
  OnDestroy,
  Output,
} from '@angular/core';
import { mergeMap, of, take, delay, Subscription, map } from 'rxjs';
import { DEFAULT_EXPENSE_FORM_GROUP } from '../../constans';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TypeExpense } from '@app/interfaces';
import { RefundService, VisaService } from '@app/shared/services';
import { CredentialsService } from '@app/features/auth/credentials.service';
import { NgFor, NgIf, UpperCasePipe, NgClass } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-step1-expenses',
  standalone: true,
  imports: [NgIf, NgFor, ReactiveFormsModule, UpperCasePipe, NgClass],
  templateUrl: './step1-expenses.component.html',
  styleUrl: './step1-expenses.component.scss',
})
export class Step1ExpensesComponent implements OnInit, OnDestroy {
  @Output() completed = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private refund = inject(RefundService);
  private credential = inject(CredentialsService);
  private visa = inject(VisaService);
  private _route = inject(ActivatedRoute);

  public idAssist = this._route.snapshot.params['id'];
  public _credential: any = this.credential.credentials;
  public countExpenses: string | null = '0';

  form!: FormGroup;
  expenseTypes: TypeExpense[] = [];
  currencyCodes: string[] = [];
  loading = false;

  private subs: Subscription = new Subscription();

  get expenseForm(): FormArray {
    return this.form.get('expenses') as FormArray;
  }

  get expenseFormLength(): number {
    return this.expenseForm.length;
  }

  get username(): string {
    return this.credential.credentials?.name ?? '';
  }

  ngOnInit(): void {
    this.createForm();

    this.subs.add(
      this.visa
        .getAllCurrency()
        .subscribe((data) => (this.currencyCodes = data)),
    );
    this.countExpenses = localStorage.getItem('expenses');

    this.subs.add(
      this.refund.assistance$
        .pipe(
          mergeMap((a) => {
            if (a) return of(a);
            return this.refund.getAssistanceById(
              this.idAssist.toString(),
              this._credential.crmObjId,
            );
          }),
          mergeMap((a) => {
            return this.refund
              .getTypeExpense(1)
              .pipe(map((types) => ({ assistance: a, types })));
          }),
          delay(0),
        )
        .subscribe(({ assistance, types }) => {
          const excludedTypes = [
            'Cash back',
            'Daño a terceros',
            'Transferencia fondos',
            'compra de dispositivos electrónicos',
            'Tech protección',
          ];

          const filteredTypes = types.filter(
            (t: TypeExpense) => !excludedTypes.includes(t.expenseName),
          );

          const medicalTypes: TypeExpense[] = [
            { id: 'm1', expenseName: 'Consulta Médica' } as any,
            { id: 'm2', expenseName: 'Medicamentos' } as any,
            { id: 'm3', expenseName: 'Hospitalización' } as any,
            { id: 'm4', expenseName: 'Exámenes y Laboratorios' } as any,
            { id: 'm5', expenseName: 'Atención Odontológica' } as any,
          ];

          // 1. Cargamos las opciones primero
          this.expenseTypes = [...medicalTypes, ...filteredTypes];

          // 2. Poblamos el formulario después de tener las opciones
          this.loadPreviousExpenses(assistance.expenseassistence);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private createForm() {
    this.form = this.fb.group({
      expenses: this.fb.array([], Validators.required),
    });
  }

  private loadPreviousExpenses(expenses: any[]) {
    const array = this.expenseForm;

    array.clear();

    if (!expenses || expenses.length < 1) {
      array.push(this.fb.group(DEFAULT_EXPENSE_FORM_GROUP));
      return;
    }

    expenses.forEach((e) => {
      const fg = this.fb.group(DEFAULT_EXPENSE_FORM_GROUP);

      const currency = e.currencyExpense
        ? e.currencyExpense.toUpperCase()
        : 'USD';
      const isNoDecimalCurrency = ['COP', 'CLP', 'JPY', 'PYG'].includes(
        currency,
      );
      const numericValue = Number(e.priceExpense) || 0;

      const formatter = new Intl.NumberFormat(
        this.getLocaleByCurrency(currency),
        {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: isNoDecimalCurrency ? 0 : 2,
        },
      );

      const parts = formatter.formatToParts(numericValue);
      let formattedPrice = '';
      for (const p of parts) {
        if (p.type === 'group') {
          formattedPrice += '.';
        } else if (p.type === 'decimal') {
          formattedPrice += ',';
        } else {
          formattedPrice += p.value;
        }
      }

      fg.setValue({
        typeExpense: e.typeExpense,
        dateExpense: e.dateExpense?.split('T')[0],
        valueExpense: formattedPrice,
        currency: e.currencyExpense,
        id: e.idExpense,
      });

      fg.disable();
      array.push(fg);
    });
  }

  addExpense() {
    if (this.expenseFormLength < 10) {
      this.expenseForm.push(this.fb.group(DEFAULT_EXPENSE_FORM_GROUP));
    }
  }

  deleteExpense(i: number) {
    if (this.expenseFormLength > 1) this.expenseForm.removeAt(i);
  }

  getLocaleByCurrency(currency: string): string {
    const locales: { [key: string]: string } = {
      COP: 'es-CO',
      EUR: 'es-ES',
      USD: 'en-US',
      CLP: 'es-CL',
      ARS: 'es-AR',
      BRL: 'pt-BR',
      MXN: 'es-MX',
      PEN: 'es-PE',
      PYG: 'es-PY',
      UYU: 'es-UY',
      VES: 'es-VE',
      BOB: 'es-BO',
      PAB: 'es-PA',
      NIO: 'es-NI',
      CAD: 'en-CA',
      JPY: 'ja-JP',
      CHF: 'de-CH',
      TRY: 'tr-TR',
      SEK: 'sv-SE',
      PLN: 'pl-PL',
      CZK: 'cs-CZ',
      RUB: 'ru-RU',
      AUD: 'en-AU',
      NZD: 'en-NZ',
    };
    return locales[currency] || 'en-US';
  }

  onCurrencyChange(index: number) {
    const control = this.expenseForm.at(index).get('valueExpense');
    if (control?.value) {
      this.onCurrencyInput({ target: { value: control.value } }, index);
    }
  }

  onCurrencyInput(event: any, index: number) {
    const inputVal = event.target.value;

    if (!inputVal) {
      this.expenseForm
        .at(index)
        .get('valueExpense')
        ?.patchValue('', { emitEvent: false });
      event.target.value = '';
      return;
    }

    const currencyControl = this.expenseForm.at(index).get('currency');
    const currency = currencyControl?.value
      ? currencyControl.value.toUpperCase()
      : 'USD';

    const isNoDecimalCurrency = ['COP', 'CLP', 'JPY', 'PYG'].includes(currency);

    const locale = this.getLocaleByCurrency(currency);
    
    // Forzar punto para miles y coma para decimales
    const groupSep = '.';
    const decimalSep = ',';

    const inputWithoutGroupSep = inputVal.split(groupSep).join('');

    let integerPart = '';
    let decimalPart = '';
    let foundDecimal = false;

    for (const char of inputWithoutGroupSep) {
      if (/[0-9]/.test(char)) {
        if (foundDecimal) {
          if (decimalPart.length < 2) {
            decimalPart += char;
          }
        } else {
          integerPart += char;
        }
      } else if (
        !isNoDecimalCurrency &&
        !foundDecimal &&
        (char === decimalSep)
      ) {
        foundDecimal = true;
      }
    }

    if (!integerPart && !foundDecimal) {
      this.expenseForm
        .at(index)
        .get('valueExpense')
        ?.patchValue('', { emitEvent: false });
      event.target.value = '';
      return;
    }

    const numericInt = parseInt(integerPart || '0', 10);

    const intFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    const formattedParts = intFormatter.formatToParts(numericInt);
    let formatted = '';
    let lastIntegerIndex = -1;
    for (let i = formattedParts.length - 1; i >= 0; i--) {
      if (formattedParts[i].type === 'integer') {
        lastIntegerIndex = i;
        break;
      }
    }

    for (let i = 0; i < formattedParts.length; i++) {
      if (formattedParts[i].type === 'group') {
        formatted += groupSep;
      } else {
        formatted += formattedParts[i].value;
      }
      
      if (i === lastIntegerIndex && foundDecimal && !isNoDecimalCurrency) {
        formatted += decimalSep + decimalPart;
      }
    }

    this.expenseForm
      .at(index)
      .get('valueExpense')
      ?.patchValue(formatted, { emitEvent: false });
    event.target.value = formatted;
  }

  canContinue(): boolean {
    if (this.expenseFormLength === 0) return false;

    let isFormValid = true;
    for (let i = 0; i < this.expenseFormLength; i++) {
      const control = this.expenseForm.at(i);
      if (!control.disabled && control.invalid) {
        isFormValid = false;
        break;
      }
    }

    return isFormValid;
  }

  onSubmit() {
    if (!this.canContinue() || this.loading) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;

    this.refund.assistance$.pipe(take(1)).subscribe({
      next: (assist) => {
        if (!assist) {
          this.loading = false;
          return;
        }

        const newControls = this.expenseForm.controls.filter(
          (c) => !c.disabled,
        );

        const newRecords = newControls.map((control: any) => {
          const x = control.getRawValue();
          const currency = x.currency ? x.currency.toUpperCase() : 'USD';
          const isNoDecimalCurrency = ['COP', 'CLP', 'JPY', 'PYG'].includes(
            currency,
          );
          let parsedPrice = 0;
          if (isNoDecimalCurrency) {
            parsedPrice = parseInt(
              String(x.valueExpense).replace(/[^0-9]/g, ''),
              10,
            );
          } else {
            const groupSep = '.';
            const decimalSep = ',';
            const inputWithoutGroupSep = String(x.valueExpense)
              .split(groupSep)
              .join('');

            let integerPart = '';
            let decimalPart = '';
            let foundDecimal = false;
            for (const char of inputWithoutGroupSep) {
              if (/[0-9]/.test(char)) {
                if (foundDecimal) {
                  decimalPart += char;
                } else {
                  integerPart += char;
                }
              } else if (
                !foundDecimal &&
                (char === decimalSep)
              ) {
                foundDecimal = true;
              }
            }
            parsedPrice = parseFloat(
              `${integerPart || '0'}.${decimalPart || '0'}`,
            );
          }

          return {
            DateCreate: new Date().toISOString(),
            TypeExpense: x.typeExpense,
            DateExpense: x.dateExpense,
            PriceExpense: parsedPrice,
            CurrencyExpense: x.currency,
            NumberCase: assist.numberCase,
            IdAssistenceMaster: assist.idAssistence,
            Status: true,
            StatusOdoo: 'En curso',
          };
        });

        if (newRecords.length > 0) {
          this.refund
            .addExpense(newRecords)
            .pipe(
              mergeMap(() =>
                this.refund.getAssistanceById(
                  this.idAssist.toString(),
                  this._credential.crmObjId,
                ),
              ),
            )
            .subscribe({
              next: (updatedAssist: any) => {
                const dbExpenses = updatedAssist.expenseassistence || [];
                const existingIds = this.expenseForm.controls
                  .filter((c) => c.disabled && c.getRawValue().id)
                  .map((c) => c.getRawValue().id);

                const newlySavedDbExpenses = dbExpenses.filter(
                  (e: any) => !existingIds.includes(e.idExpense),
                );

                newControls.forEach((ctrl, idx) => {
                  if (newlySavedDbExpenses[idx]) {
                    ctrl.patchValue({
                      id: newlySavedDbExpenses[idx].idExpense,
                    });
                    ctrl.disable();
                  }
                });

                this.loading = false;
                this.completed.emit();
              },
              error: (err) => {
                console.error('Error al guardar o refrescar los gastos:', err);
                this.loading = false;
              },
            });
        } else {
          this.loading = false;
          this.completed.emit();
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }
}
